import React, { useState, useRef, useEffect } from 'react';
import { useDataChannel } from '@livekit/components-react';

/**
 * Chat panel component that displays in the bottom right of the video conference
 */
export default function ChatPanel({ isOpen, roomName = 'default-room', userIdentity = 'You' }) {
  const [messages, setMessages] = useState([
    { id: 1, sender: 'System', text: 'Welcome to the chat!' },
  ]);
  // Get local participant information
  const [participantName, setParticipantName] = useState(userIdentity);
  
  // Update participant name if userIdentity changes
  useEffect(() => {
    setParticipantName(userIdentity);
  }, [userIdentity]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);
  
  // Use LiveKit's data channel with a specific topic for chat messages
  // This will filter messages by the chat topic
  const chatTopic = `chat-${roomName}`;
  const { send, message } = useDataChannel(chatTopic);
  
  // Log when component mounts to verify channel name
  useEffect(() => {
    console.log(`ChatPanel mounted for room: ${roomName}, using chat topic: ${chatTopic}`);
  }, [roomName, chatTopic]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Handle incoming chat messages
  useEffect(() => {
    if (!message) return;
    
    try {
      const decoded = new TextDecoder().decode(message.payload);
      const data = JSON.parse(decoded);
      
      console.log('Received chat message:', data);
      
      if (data && data.type === 'chat-message') {
        // Add the received message to our list
        setMessages(prevMessages => [
          ...prevMessages,
          {
            id: data.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            sender: data.sender || 'Unknown',
            text: data.text
          }
        ]);
      }
    } catch (error) {
      console.error('Error parsing chat message:', error);
    }
  }, [message]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputText.trim() === '') return;
    
    const messageId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const messageText = inputText.trim();
    
    // Add the new message to our local list
    setMessages([
      ...messages,
      {
        id: messageId,
        sender: participantName,
        text: messageText,
      },
    ]);
    
    // Send the message to other participants
    const messageData = {
      type: 'chat-message',
      id: messageId,
      sender: participantName,
      text: messageText
    };
    
    console.log('Sending chat message:', messageData);
    try {
      const encodedMessage = new TextEncoder().encode(JSON.stringify(messageData));
      send(encodedMessage);
      console.log('Chat message sent successfully');
    } catch (error) {
      console.error('Error sending chat message:', error);
    }
    
    // Clear the input
    setInputText('');
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .chat-panel {
          position: absolute;
          bottom: 70px; /* Position above the control bar */
          right: 10px;
          width: 230px;
          height: 40vh;
          background-color: rgba(0, 0, 0, 0.7);
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 5;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .chat-header {
          padding: 10px;
          background-color: rgba(0, 0, 0, 0.5);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .chat-header h3 {
          margin: 0;
          color: white;
          font-size: 16px;
        }
        
        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .message {
          background-color: rgba(255, 255, 255, 0.1);
          padding: 8px 12px;
          border-radius: 8px;
          max-width: 85%;
          align-self: flex-start;
        }
        .own-message {
          align-self: flex-end;
          background-color: rgba(100, 100, 255, 0.2); /* light blue tone */
        }
        
        .message-sender {
          font-size: 12px;
          font-weight: bold;
          color: #ccc;
          margin-bottom: 4px;
        }
        
        .message-text {
          color: white;
          word-break: break-word;
        }
        
        .message-input-form {
          display: flex;
          padding: 10px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .message-input {
          flex: 1;
          padding: 8px 12px;
          border-radius: 20px;
          border: none;
          background-color: rgba(255, 255, 255, 0.1);
          color: white;
          outline: none;
        }
        
        .message-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
        
        .send-button {
          background-color: transparent;
          border: none;
          color: white;
          padding: 0 10px;
          cursor: pointer;
        }
      `}</style>
      <div className="chat-panel">
        <div className="chat-header">
          <h3>Chat</h3>
        </div>
        
        <div className="messages-container">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === participantName ? 'own-message' : ''}`}
            >
              <div className="message-sender">{message.sender}</div>
              <div className="message-text">{message.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        <form className="message-input-form" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type a message..."
            className="message-input"
          />
          <button type="submit" className="send-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </>
  );
}
